import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ColumnDef } from '@tanstack/react-table';
import PageHeader from '../../../Common/PageHeader';
import Footer from '../../../Layout/Footer';
import DatePickerWithRange from '../../../../../components/Common/UI/DateRange';
import AccordionTable, { IAccordionData } from '../../../Common/AccordionTable';
import { Input } from '../../../../../components/Common/UI/Input';
import Icons from '../../../../../components/Common/Icons/Icons';
import { initialPeriod, IPeriod } from '../../Notices';

export interface StatementPageTemplateProps<D> {
  icon: React.ReactNode;
  title: string;
  tableProps: {
    data: IAccordionData<D>[];
    tableCol: ColumnDef<D>[];
  };
  searchSuggestions?: string[];
}

function StatementPageTemplate<D extends { type?: string; pdfSrc?: string }>({
  icon,
  title,
  tableProps,
  searchSuggestions = [],
}: StatementPageTemplateProps<D>) {
  const [active, setActive] = useState<number | undefined>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(initialPeriod);

  const [searchVal, setSearchVal] = useState('');
  const [open, setOpen] = useState(false);

  const handleOnChange = (from?: Date, to?: Date) => {
    if (from && to) {
      setSelectedPeriod({
        from: dayjs(from).format('MMM DD YYYY'),
        to: dayjs(to).format('MMM DD YYYY'),
      });
    }
  };

  const handleActive = (index?: number) => {
    setActive(index);
  };

  const filtered = useMemo(() => {
    if (!searchVal.trim() || searchSuggestions.length === 0) return [];

    return searchSuggestions.filter(item => item.toLowerCase().includes(searchVal.toLowerCase()));
  }, [searchVal, searchSuggestions]);

  const tableData = useMemo(() => {
    let filteredGroups = tableProps.data;

    if (!(selectedPeriod.from === initialPeriod.from && selectedPeriod.to === initialPeriod.to)) {
      filteredGroups = tableProps.data.filter(({ year }) =>
        dayjs(`01-01-${year}`).isBetween(dayjs(selectedPeriod.from), dayjs(selectedPeriod.to), 'year', '[]'),
      );
    }

    if (searchVal.trim()) {
      const searchLower = searchVal.toLowerCase();

      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter((item: any) => {
            if (searchLower === 'payroll') return item.type === 'Payroll';

            return Object.values(item).some(val => String(val).toLowerCase().includes(searchLower));
          }),
        }))
        .filter(group => group.data.length > 0);
    } else {
      filteredGroups = filteredGroups
        .map(group => ({
          ...group,
          data: group.data.filter((item: any) => !item.type),
        }))
        .filter(group => group.data.length > 0);
    }

    return filteredGroups;
  }, [selectedPeriod, tableProps.data, searchVal]);

  const showSearch = searchSuggestions.length > 0;

  return (
    <div>
      <PageHeader
        icon={icon}
        title={title}
        actions={
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="relative max-w-60">
                <Input
                  placeholder="Search"
                  variant="home"
                  inputClassName="border-[#FFFFFF]"
                  value={searchVal}
                  icon={<Icons.Search />}
                  onBlur={() => setTimeout(() => setOpen(false), 150)}
                  onChange={e => {
                    setSearchVal(e.target.value);
                    setOpen(!!e.target.value);
                  }}
                />

                {open && searchVal.trim() && filtered.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow">
                    {filtered.map(item => (
                      <div
                        key={item}
                        tabIndex={0}
                        role="button"
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                        onMouseDown={() => {
                          setSearchVal(item);
                          setOpen(false);
                        }}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DatePickerWithRange
              variant="transparent"
              from={dayjs(initialPeriod.from).toDate()}
              to={dayjs(initialPeriod.to).toDate()}
              handleOnChange={handleOnChange}
            />
          </div>
        }
      />
      <div className="px-[20px] grid gap-[20px]">
        {(tableData || []).length ? (
          <>
            <AccordionTable<D>
              data={tableData}
              tableCol={tableProps.tableCol}
              handleActive={handleActive}
              active={active}
            />
            <Footer />
          </>
        ) : (
          <div className="font-bold text-[18px] text-center my-[100px]">
            There is no statement available for this data range.
          </div>
        )}
      </div>
    </div>
  );
}

export default StatementPageTemplate;
